// 📄 components/interfaces/InterfaceCatalogGrid.tsx
// Grid filtrable de tarjetas del Catálogo de Interfaces/Canales — compartido
// entre la sección de Admin (CRUD completo, ver InterfaceCatalogSection.tsx)
// y las vistas de solo lectura (Herramientas del proyecto y Ariba Toolbox):
// la UI de consulta/filtro/copiar es idéntica en todos lados, solo cambia si
// se muestran los botones de Editar/Eliminar. Mismo patrón que
// [[TransactionCatalogGrid.tsx]].
//
// Caso de uso real: el usuario sabe QUÉ documento le llegó mal ("un
// contrato"), no en qué transacción buscarlo — por eso el título de la card
// es `nombreTecnico` (el valor a copiar/pegar), no el código de transacción,
// y el filtro/búsqueda priorizan `objetoNegocio`.
import { useMemo, useState } from 'react';
import { Search, Copy, Check, Pencil, Trash2, ExternalLink } from 'lucide-react';
import {
  InterfaceChannel,
  OBJETOS_NEGOCIO,
  SOLUCIONES_INTERFAZ,
  PLATAFORMAS_INTERFAZ,
  DIRECCIONES_INTERFAZ,
  objetoNegocioMeta,
  solucionInterfazMeta,
  plataformaInterfazMeta,
  direccionInterfazMeta
} from '../../services/catalogoInterfacesService.ts';

// Item del grid: InterfaceChannel (catálogo global) con los campos extra
// que solo trae un desarrollo Z (ver interfazZService.ts) — todos opcionales
// a propósito, así los consumidores del catálogo global siguen pasando
// InterfaceChannel[] tal cual. Mismo patrón que TransactionCatalogGrid.tsx /
// BadiCatalogGrid.tsx.
type InterfaceCatalogItem = InterfaceChannel & {
  responsable?: string;
  sistemasDisponibles?: string[];
};

interface InterfaceCatalogGridProps {
  interfaces: InterfaceCatalogItem[];
  // true = catálogo de interfaces Z de un proyecto: agrega el badge "Z" y
  // el bloque de responsable/sistemas bajo la descripción de cada card.
  esZ?: boolean;
  readOnly?: boolean;
  onEdit?: (item: InterfaceChannel) => void;
  onDelete?: (item: InterfaceChannel) => void;
  // Navega al Catálogo de Transacciones — el destino real depende de dónde
  // está montado este grid (Admin / dentro de un proyecto / Ariba Toolbox),
  // así que cada consumidor decide qué significa "ir ahí". Sin esta prop el
  // código de transacción con `transaccionRefId` no se renderiza como link.
  onVerEnTransacciones?: () => void;
}

// Mismos tokens de color que ya usa el resto del proyecto (ver
// KnownErrorFormModal.tsx / BadiCatalogGrid.tsx) — verde/teal, ámbar,
// morado, sin hex hardcodeado.
const DIRECCION_ESTILOS: Record<string, string> = {
  ingreso: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  salida: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  bidireccional: 'text-purple-300 bg-purple-500/10 border-purple-500/30'
};

export default function InterfaceCatalogGrid({ interfaces, esZ = false, readOnly = false, onEdit, onDelete, onVerEnTransacciones }: InterfaceCatalogGridProps) {
  const [busqueda, setBusqueda] = useState('');
  const [filtroObjeto, setFiltroObjeto] = useState('');
  const [filtroSolucion, setFiltroSolucion] = useState('');
  const [filtroPlataforma, setFiltroPlataforma] = useState('');
  const [filtroDireccion, setFiltroDireccion] = useState('');
  const [copiadoId, setCopiadoId] = useState<string | null>(null);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return interfaces.filter((i) => {
      if (filtroObjeto && i.objetoNegocio !== filtroObjeto) return false;
      if (filtroSolucion && i.solucion !== filtroSolucion) return false;
      if (filtroPlataforma && i.plataforma !== filtroPlataforma) return false;
      if (filtroDireccion && i.direccion !== filtroDireccion) return false;
      if (!q) return true;
      const objetoLabel = objetoNegocioMeta(i.objetoNegocio)?.label || '';
      return [i.transaccion, i.textoBreve, i.nombreTecnico, i.descripcion, objetoLabel, ...(i.palabrasClave || [])]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [interfaces, busqueda, filtroObjeto, filtroSolucion, filtroPlataforma, filtroDireccion]);

  const handleCopiar = async (i: InterfaceChannel) => {
    try {
      await navigator.clipboard.writeText(i.nombreTecnico);
      setCopiadoId(i.id);
      setTimeout(() => setCopiadoId((prev) => (prev === i.id ? null : prev)), 1500);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por transacción, nombre técnico o texto..."
            className={`${inputClass} pl-9`}
          />
        </div>
        <select className={inputClass} value={filtroObjeto} onChange={(e) => setFiltroObjeto(e.target.value)}>
          <option value="">Todos los objetos</option>
          {OBJETOS_NEGOCIO.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
        <select className={inputClass} value={filtroSolucion} onChange={(e) => setFiltroSolucion(e.target.value)}>
          <option value="">Todas las soluciones</option>
          {SOLUCIONES_INTERFAZ.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <select className={inputClass} value={filtroPlataforma} onChange={(e) => setFiltroPlataforma(e.target.value)}>
          <option value="">Todas las plataformas</option>
          {PLATAFORMAS_INTERFAZ.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        <select className={inputClass} value={filtroDireccion} onChange={(e) => setFiltroDireccion(e.target.value)}>
          <option value="">Todas las direcciones</option>
          {DIRECCIONES_INTERFAZ.map((d) => (
            <option key={d.id} value={d.id}>{d.label}</option>
          ))}
        </select>
      </div>

      {filtrados.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
          {interfaces.length === 0 ? 'Todavía no hay interfaces/canales registrados en el catálogo.' : 'Ninguna interfaz coincide con el filtro.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtrados.map((item) => {
            const objMeta = objetoNegocioMeta(item.objetoNegocio);
            const solMeta = solucionInterfazMeta(item.solucion);
            const platMeta = plataformaInterfazMeta(item.plataforma);
            const dirMeta = direccionInterfazMeta(item.direccion);
            const tieneRef = !!item.transaccionRefId && !!onVerEnTransacciones;
            return (
              <div key={item.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <h4
                    title={item.nombreTecnico}
                    className="text-sm font-mono font-semibold text-white leading-snug truncate min-w-0"
                  >
                    {item.nombreTecnico}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleCopiar(item)}
                    title="Copiar Nombre técnico"
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiadoId === item.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>

                <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                  <span className="text-gray-600">Buscar en:</span>
                  {tieneRef ? (
                    <button
                      type="button"
                      onClick={onVerEnTransacciones}
                      title="Ver en el Catálogo de Transacciones"
                      className="font-mono text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 transition-colors"
                    >
                      {item.transaccion}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </button>
                  ) : (
                    <span className="font-mono text-gray-300">{item.transaccion}</span>
                  )}
                  {item.campoBusqueda && (
                    <>
                      <span className="text-gray-600">→</span>
                      <span className="text-gray-300">{item.campoBusqueda}</span>
                    </>
                  )}
                </p>

                <div className="flex flex-wrap items-center gap-1.5">
                  {esZ && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                  )}
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                    objMeta ? 'text-blue-300 bg-blue-500/10 border-blue-500/30' : 'text-gray-400 bg-white/5 border-white/20'
                  }`}>
                    {objMeta ? objMeta.label : 'Sin clasificar'}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                    {solMeta.label}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-sky-300 bg-sky-500/10 border-sky-500/30">
                    {platMeta.label}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${DIRECCION_ESTILOS[item.direccion]}`}>
                    {dirMeta.labelCorta}
                  </span>
                </div>

                <p className="text-xs text-gray-300 font-medium leading-relaxed">{item.textoBreve}</p>

                {item.descripcion && item.descripcion !== item.textoBreve && (
                  <p className="text-xs text-gray-400 leading-relaxed line-clamp-3">{item.descripcion}</p>
                )}

                {esZ && (item.responsable || (item.sistemasDisponibles && item.sistemasDisponibles.length > 0)) && (
                  <div className="pt-2 border-t border-white/5 space-y-1.5">
                    {item.responsable && (
                      <p className="text-[11px] text-gray-500">Responsable: <span className="text-gray-300">{item.responsable}</span></p>
                    )}
                    {item.sistemasDisponibles && item.sistemasDisponibles.length > 0 && (
                      <div className="flex items-center gap-1">
                        {item.sistemasDisponibles.map((s) => (
                          <span key={s} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/30">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!readOnly && (onEdit || onDelete) && (
                  <div className="flex items-center justify-end gap-1.5 pt-2 mt-auto border-t border-white/5">
                    {onEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(item)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={() => onDelete(item)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
